import moment from 'moment';
import 'moment/locale/ru';

import { collectCrmClientActivesMatchIds } from '#app/api/clients/crmClientActivesUserId';
import type { Client } from '#app/api/types/api.types';
import type { FinanceGroup } from '#app/domain/clients/financeGroup';
import { getActivesItemValuation, type ActivesValuationItem } from '#app/domain/funnel/activesValuation';

export type { FinanceGroup };

export type ClientAtonValuationSnapshot = {
	sum: number;
	/** Самая поздняя `date` из строк ответа (YYYY-MM-DD или YYYY-MM). */
	asOfDate: string | null;
};

/** CRM client.id (+ platform user_id) + union_user_id из фин. группы. */
export function collectClientValuationUserIds(
	clientId: number,
	financeGroups: FinanceGroup[] | null | undefined,
	client?: Client | null
): number[] {
	const ids = new Set<number>();
	if (client) {
		collectCrmClientActivesMatchIds(client).forEach((id) => ids.add(id));
	} else {
		const primary = Number(clientId);
		if (Number.isFinite(primary) && primary > 0) {
			ids.add(primary);
		}
	}
	for (const row of financeGroups ?? []) {
		const unionId = Number(
			row?.union_user_id ?? (row as FinanceGroup & { unionUser?: { id?: number } }).unionUser?.id
		);
		if (Number.isFinite(unionId) && unionId > 0) {
			ids.add(unionId);
		}
	}
	return [...ids];
}

function parseValuationMoment(rawDate: unknown): moment.Moment | null {
	if (rawDate == null || rawDate === '') {
		return null;
	}
	const parsed = moment(String(rawDate), ['YYYY-MM-DD', 'YYYY-MM', 'YYYY.MM'], true);
	return parsed.isValid() ? parsed : null;
}

/** Последняя актуальная дата оценки по всем user_id клиента (фин. группа). */
export function pickLatestValuationDate(rows: ActivesValuationItem[]): string | null {
	let latest: moment.Moment | null = null;
	for (const row of rows) {
		const rowDate = (row as ActivesValuationItem & { date?: unknown }).date;
		const parsed = parseValuationMoment(rowDate);
		if (!parsed) {
			continue;
		}
		if (!latest || parsed.isAfter(latest, 'day')) {
			latest = parsed;
		}
	}
	return latest ? latest.format('YYYY-MM-DD') : null;
}

/** «(май 2026)» — месяц словами, год цифрами. */
export function formatValuationMonthYearParenthetical(asOfDate: string | null | undefined): string | null {
	const parsed = parseValuationMoment(asOfDate);
	if (!parsed) {
		return null;
	}
	const monthName = parsed.locale('ru').format('MMMM');
	return `(${monthName} ${parsed.year()})`;
}

export { getActivesItemValuation };
export type { ActivesValuationItem };
